import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import ResearchShell from '../components/research/ResearchShell'
import type { IdeaStatus, IdeaSymbolContext, InvestmentIdea } from '../types/research'
import {
  deleteIdea,
  getIdeaSymbolContext,
  isResearchUsingMockData,
  listIdeas,
  saveIdea,
} from '../lib/research-api'

const STATUS_STYLES: Record<IdeaStatus, string> = {
  watching: 'bg-sky-500/10 text-sky-600 dark:text-sky-400',
  active: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  closed: 'bg-slate-500/10 text-slate-500 dark:text-slate-400',
}

interface IdeaFormState {
  id?: string
  title: string
  thesis: string
  symbols: string
  status: IdeaStatus
  tags: string
  notes: string
}

const EMPTY_FORM: IdeaFormState = { title: '', thesis: '', symbols: '', status: 'watching', tags: '', notes: '' }

function SymbolContextRow({ ctx }: { ctx: IdeaSymbolContext }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs py-1.5">
      <Link
        to={`/dashboard/financial-app/fundamentals/${ctx.symbol}`}
        className="font-semibold text-blue-600 dark:text-blue-400 hover:underline"
      >
        {ctx.symbol}
      </Link>
      {ctx.price != null && (
        <span className="text-slate-600 dark:text-slate-300">
          ${ctx.price.toFixed(2)}
          {ctx.changePercent != null && (
            <span className={ctx.changePercent >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}>
              {' '}
              {ctx.changePercent >= 0 ? '+' : ''}
              {ctx.changePercent}%
            </span>
          )}
        </span>
      )}
      {ctx.nextEarningsDate && (
        <span
          className={`${
            (ctx.daysUntilEarnings ?? 99) <= 14
              ? 'text-amber-600 dark:text-amber-400 font-medium'
              : 'text-slate-500'
          }`}
        >
          📅 Earnings {ctx.nextEarningsDate}
          {ctx.daysUntilEarnings != null && ` (${ctx.daysUntilEarnings}d)`}
        </span>
      )}
      {ctx.latestFilings.map((f) => (
        <a
          key={f.accessionNumber}
          href={f.edgarUrl}
          target="_blank"
          rel="noreferrer"
          className="text-blue-600 dark:text-blue-400 hover:underline"
          title={f.description}
        >
          {f.formType} ↗
        </a>
      ))}
    </div>
  )
}

export default function IdeasPage() {
  const navigate = useNavigate()
  const [ideas, setIdeas] = useState<InvestmentIdea[]>([])
  const [contexts, setContexts] = useState<Record<string, IdeaSymbolContext>>({})
  const [loading, setLoading] = useState(true)
  const [isMock, setIsMock] = useState(false)
  const [form, setForm] = useState<IdeaFormState | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    ;(async () => {
      const list = await listIdeas()
      setIdeas(list)
      setIsMock(isResearchUsingMockData())
      setLoading(false)
      hydrate(list)
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function hydrate(list: InvestmentIdea[]) {
    const symbols = Array.from(new Set(list.flatMap((i) => i.symbols)))
    symbols.forEach((sym) => {
      getIdeaSymbolContext(sym)
        .then((ctx) => setContexts((prev) => ({ ...prev, [sym]: ctx })))
        .catch(() => {})
    })
  }

  async function handleSave() {
    if (!form || !form.title.trim()) return
    setSaving(true)
    try {
      const symbols = form.symbols
        .split(/[,\s]+/)
        .map((s) => s.trim().toUpperCase())
        .filter(Boolean)
      const saved = await saveIdea({
        id: form.id,
        title: form.title.trim(),
        thesis: form.thesis.trim(),
        status: form.status,
        symbols,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
        notes: form.notes.trim(),
        source: 'manual',
      })
      setIdeas((prev) => {
        const exists = prev.some((i) => i.id === saved.id)
        return exists ? prev.map((i) => (i.id === saved.id ? saved : i)) : [saved, ...prev]
      })
      hydrate([saved])
      setForm(null)
    } finally {
      setSaving(false)
    }
  }

  async function handleDelete(id: string) {
    await deleteIdea(id)
    setIdeas((prev) => prev.filter((i) => i.id !== id))
  }

  function editIdea(idea: InvestmentIdea) {
    setForm({
      id: idea.id,
      title: idea.title,
      thesis: idea.thesis,
      symbols: idea.symbols.join(', '),
      status: idea.status,
      tags: idea.tags.join(', '),
      notes: idea.notes,
    })
  }

  function flagWithOpus(idea: InvestmentIdea) {
    const params = new URLSearchParams({ task: 'flag-thesis', thesis: idea.thesis || idea.title })
    if (idea.symbols.length) params.set('symbols', idea.symbols.join(','))
    navigate(`/dashboard/financial-app/research?${params}`)
  }

  const inputCls =
    'w-full bg-slate-100 dark:bg-slate-900 text-slate-900 dark:text-white text-sm rounded-lg px-3 py-2 border border-slate-300 dark:border-slate-600 focus:border-blue-500 outline-none placeholder-slate-400 dark:placeholder-slate-500'

  return (
    <ResearchShell showMockBanner={isMock && !loading}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Investment ideas</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Track theses with live earnings dates and links to the latest filings
          </p>
        </div>
        <button
          onClick={() => setForm({ ...EMPTY_FORM })}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
        >
          + New idea
        </button>
      </div>

      {/* Editor */}
      {form && (
        <div className="bg-white dark:bg-slate-800 border border-blue-500/40 rounded-xl p-4 mb-5">
          <h3 className="text-sm font-semibold text-slate-900 dark:text-white mb-3">
            {form.id ? 'Edit idea' : 'New idea'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Title (e.g. Long semis vol into earnings)"
              className={inputCls}
            />
            <input
              value={form.symbols}
              onChange={(e) => setForm({ ...form, symbols: e.target.value })}
              placeholder="Symbols — comma separated (e.g. NVDA, MU)"
              className={inputCls}
            />
          </div>
          <textarea
            value={form.thesis}
            onChange={(e) => setForm({ ...form, thesis: e.target.value })}
            placeholder="Thesis — what do you expect to happen and why?"
            rows={3}
            className={`${inputCls} mb-3`}
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value as IdeaStatus })}
              className={inputCls}
            >
              <option value="watching">Watching</option>
              <option value="active">Active</option>
              <option value="closed">Closed</option>
            </select>
            <input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="Tags (e.g. vol, earnings)"
              className={inputCls}
            />
            <input
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Notes"
              className={inputCls}
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleSave}
              disabled={saving || !form.title.trim()}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 dark:disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              {saving ? 'Saving…' : 'Save idea'}
            </button>
            <button
              onClick={() => setForm(null)}
              className="px-4 py-2 bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 text-sm font-medium rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* List */}
      {loading && (
        <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 animate-pulse">
          <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-1/3 mb-3" />
          <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3" />
        </div>
      )}

      {!loading && ideas.length === 0 && !form && (
        <div className="text-center py-14 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl">
          <div className="text-4xl mb-3">💡</div>
          <p className="text-slate-900 dark:text-white font-medium">No ideas tracked yet</p>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1 max-w-md mx-auto">
            Add an idea manually, or flag candidates from the Screener and Opus Research tabs — each
            tracked symbol shows its next earnings date and latest filings.
          </p>
        </div>
      )}

      <div className="space-y-4">
        {ideas.map((idea) => (
          <div key={idea.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900 dark:text-white">{idea.title}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLES[idea.status]}`}>
                    {idea.status}
                  </span>
                  {idea.source === 'opus-flag' && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-500/10 text-indigo-600 dark:text-indigo-400">
                      🧠 Opus flagged
                    </span>
                  )}
                  {idea.tags.map((t) => (
                    <span key={t} className="px-2 py-0.5 rounded-md text-xs bg-slate-200/50 dark:bg-slate-700/50 text-slate-500 dark:text-slate-400">
                      {t}
                    </span>
                  ))}
                </div>
                {idea.thesis && (
                  <p className="text-sm text-slate-600 dark:text-slate-300 mt-1.5 leading-relaxed">{idea.thesis}</p>
                )}
                {idea.notes && <p className="text-xs text-slate-500 mt-1">{idea.notes}</p>}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => flagWithOpus(idea)}
                  className="px-2.5 py-1.5 text-xs bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400 rounded-lg transition-colors"
                  title="Ask Opus to flag securities matching this thesis"
                >
                  🧠 Flag with Opus
                </button>
                <button
                  onClick={() => editIdea(idea)}
                  className="px-2.5 py-1.5 text-xs bg-slate-200/70 dark:bg-slate-700/50 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-lg transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(idea.id)}
                  className="px-2.5 py-1.5 text-xs bg-red-500/10 hover:bg-red-500/20 text-red-600 dark:text-red-400 rounded-lg transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>

            {/* Tracked symbols with live context */}
            {idea.symbols.length > 0 && (
              <div className="mt-3 pt-2 border-t border-slate-200 dark:border-slate-700/50 divide-y divide-slate-200/60 dark:divide-slate-700/40">
                {idea.symbols.map((sym) =>
                  contexts[sym] ? (
                    <SymbolContextRow key={sym} ctx={contexts[sym]} />
                  ) : (
                    <div key={sym} className="text-xs text-slate-500 py-1.5">
                      {sym} — loading context…
                    </div>
                  )
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </ResearchShell>
  )
}
